import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { messages, type VoiceProfileDto } from '@app/shared';
import { AudioPlayer } from '../components/AudioPlayer';
import { ChatSampleFallback } from '../components/ChatSampleFallback';
import { PlainLayout } from '../components/shell/PlainLayout';
import { RecordButton } from '../components/RecordButton';
import { Button, Card, Spinner, Toast } from '../components/ui';
import type { RecorderErrorKind } from '../hooks/useVoiceRecorder';
import { useT } from '../i18n';
import { haptic } from '../lib/tma';
import { voiceApi, VoiceApiError } from '../lib/voice-client';
import { canRecordAudio, safeBackPath } from '../lib/voice-format';

type Step = 'intro' | 'sample' | 'consent' | 'creating' | 'preview';
type Note = { tone: 'warning' | 'destructive'; text: string } | null;

const SAMPLE_MAX_SECONDS = 30;
const CONSENT_MAX_SECONDS = 25;

/**
 * Spec 8.1: sample and consent recorded back to back on one screen, the voice
 * is created, the person hears a phrase in it and answers "sounds like me?".
 */
export function VoiceRecordPage() {
  const t = useT();
  const texts = messages();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const back = safeBackPath(params.get('back'));

  const [step, setStep] = useState<Step>(params.get('step') === 'preview' ? 'preview' : 'intro');
  const [sample, setSample] = useState<Blob | null>(null);
  const [consent, setConsent] = useState<Blob | null>(null);
  const [ticked, setTicked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<Note>(null);
  const [previewKey, setPreviewKey] = useState(0);
  // No microphone here (unsupported or refused): offer the bot's chat instead (spec 6.3).
  const [chatOffered, setChatOffered] = useState(() => !canRecordAudio());

  const warn = (text: string) => setNote({ tone: 'warning', text });
  const fail = (text: string = t.common.failed) => setNote({ tone: 'destructive', text });
  const onRecorderError = (kind: RecorderErrorKind) => {
    warn(kind === 'permission' ? t.voiceSetup.permission : t.common.failed);
    if (kind === 'permission') setChatOffered(true);
  };

  async function checked(kind: 'sample' | 'consent', blob: Blob): Promise<boolean> {
    setNote(null);
    setBusy(true);
    try {
      const r = await voiceApi.checkRecording(kind, blob);
      if (!r.ok && r.problem) warn(t.voiceSetup.problems[r.problem]);
      return r.ok;
    } catch {
      fail();
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function onSample(blob: Blob) {
    if (!(await checked('sample', blob))) return;
    setSample(blob);
    setConsent(null);
    setStep('consent');
  }

  async function onConsent(blob: Blob) {
    if (!sample || !(await checked('consent', blob))) return;
    setConsent(blob);
    await create(sample, blob);
  }

  async function create(s: Blob, c: Blob) {
    setNote(null);
    setStep('creating');
    try {
      const res = await voiceApi.createVoice({ sample: s, consent: c, consentAccepted: ticked });
      switch (res.kind) {
        case 'ok':
          qc.setQueryData<VoiceProfileDto>(['voice', 'profile'], res.profile);
          setPreviewKey((k) => k + 1);
          haptic('success');
          setStep('preview');
          return;
        case 'recording':
          warn(t.voiceSetup.problems[res.problem]);
          setStep(res.which);
          return;
        case 'consent_mismatch':
          warn(t.voiceSetup.consentMismatch);
          setSample(null);
          setStep('sample');
          return;
        case 'limit':
          warn(t.voiceSetup.limit);
          setStep('consent');
          return;
        case 'unavailable':
          fail(t.voiceSetup.unavailable);
          setStep('consent');
          return;
      }
    } catch (err) {
      fail(
        err instanceof VoiceApiError && err.code === 'CONSENT_OUTDATED'
          ? t.voiceSetup.outdated
          : undefined,
      );
      setStep('consent');
    }
  }

  async function confirm() {
    setBusy(true);
    try {
      qc.setQueryData<VoiceProfileDto>(['voice', 'profile'], await voiceApi.confirmVoice());
      haptic('success');
      navigate(back);
    } catch {
      fail();
    } finally {
      setBusy(false);
    }
  }

  async function redo() {
    setBusy(true);
    // A failed delete is fine: the next voice replaces this one and deletes it.
    await voiceApi.deleteVoice().catch(() => null);
    qc.setQueryData<VoiceProfileDto>(['voice', 'profile'], { status: 'none', createdAt: null });
    setBusy(false);
    setSample(null);
    setConsent(null);
    setNote(null);
    setStep('sample');
  }

  return (
    <PlainLayout title={t.voiceSetup.title} back={back}>

      {step === 'intro' && (
        <Card variant="surface" padding="lg">
          <h2 className="text-xl font-bold mb-2">{t.voiceSetup.introTitle}</h2>
          <p className="text-sm mb-3">{t.voiceSetup.introText}</p>
          <p className="text-sm text-hint mb-5">{t.voiceSetup.introManner}</p>
          {chatOffered ? (
            <p className="text-sm text-hint">{t.voiceSetup.unsupported}</p>
          ) : (
            <Button variant="primary" size="lg" fullWidth onClick={() => setStep('sample')}>
              {t.voiceSetup.start}
            </Button>
          )}
        </Card>
      )}

      {step === 'sample' && (
        <>
          <p className="text-xs font-bold uppercase tracking-[0.06em] text-hint mb-2">
            {t.voiceSetup.step1}
          </p>
          <p className="text-sm mb-2">{t.voiceSetup.step1Hint}</p>
          <Card variant="surface" padding="lg" className="mb-4">
            <p className="text-lg leading-relaxed">{texts.voiceSampleText}</p>
          </Card>
        </>
      )}

      {step === 'consent' && (
        <>
          <p className="text-xs font-bold uppercase tracking-[0.06em] text-hint mb-2">
            {t.voiceSetup.step2}
          </p>
          <p className="text-sm mb-2">{t.voiceSetup.step2Hint}</p>
          <Card variant="surface" padding="lg" className="mb-4">
            <p className="text-lg leading-relaxed">«{texts.voiceConsentPhrase}»</p>
          </Card>
          <label className="flex items-start gap-3 text-sm mb-4">
            <input
              type="checkbox"
              checked={ticked}
              onChange={(e) => setTicked(e.target.checked)}
              className="mt-0.5 w-5 h-5 flex-shrink-0 accent-[rgb(var(--ink))]"
            />
            <span>{t.voiceSetup.consentBox}</span>
          </label>
        </>
      )}

      {note && (
        <Toast tone={note.tone} className="mb-3">
          {note.text}
        </Toast>
      )}

      {(step === 'sample' || step === 'consent') &&
        (busy ? (
          <div className="flex items-center justify-center gap-2 py-3 text-sm text-hint">
            <Spinner size="sm" />
            {t.voiceSetup.checking}
          </div>
        ) : (
          <div className="space-y-2">
            <RecordButton
              key={step}
              maxSeconds={step === 'sample' ? SAMPLE_MAX_SECONDS : CONSENT_MAX_SECONDS}
              disabled={step === 'consent' && !ticked}
              onRecorded={(blob) => void (step === 'sample' ? onSample(blob) : onConsent(blob))}
              onError={onRecorderError}
            />
            {step === 'consent' && sample && consent && ticked && (
              <Button variant="secondary" fullWidth onClick={() => void create(sample, consent)}>
                {t.voiceSetup.retry}
              </Button>
            )}
          </div>
        ))}

      {chatOffered && (step === 'intro' || step === 'sample' || step === 'consent') && (
        <div className="mt-4">
          <ChatSampleFallback />
        </div>
      )}

      {step === 'creating' && (
        <div className="flex flex-col items-center gap-3 py-10 text-center">
          <Spinner size="lg" />
          <p className="text-sm text-hint">{t.voiceSetup.creating}</p>
        </div>
      )}

      {step === 'preview' && (
        <Card variant="surface" padding="lg">
          <h2 className="text-xl font-bold mb-1">{t.voiceSetup.previewTitle}</h2>
          <p className="text-sm text-hint mb-4">{t.voiceSetup.previewHint}</p>
          <div className="mb-5">
            <AudioPlayer
              queryKey={['voice', 'preview', previewKey]}
              load={() => voiceApi.getVoicePreview()}
            />
          </div>
          <div className="space-y-2">
            <Button
              variant="primary"
              size="lg"
              fullWidth
              loading={busy}
              onClick={() => void confirm()}
            >
              {t.voiceSetup.yes}
            </Button>
            <Button variant="secondary" fullWidth disabled={busy} onClick={() => void redo()}>
              {t.voiceSetup.no}
            </Button>
          </div>
        </Card>
      )}
    </PlainLayout>
  );
}
